import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { firestore } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";
import CourseCard from "../CourseCard";

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const Filters = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const FilterBtn = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: ${({ $active }) => ($active ? "rgba(15,118,110,0.10)" : "#fff")};
  color: ${({ $active }) => ($active ? "#0f766e" : "#111827")};
  font-weight: 950;
  padding: 10px 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 140ms ease, background 140ms ease;

  &:hover {
    background: ${({ $active }) => ($active ? "rgba(15,118,110,0.14)" : "#f9fafb")};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

const Count = styled.div`
  color: #6b7280;
  font-weight: 900;
  font-size: 13px;
`;

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  margin-top: 14px;
`;

const Empty = styled.div`
  padding: 14px 0;
  color: #6b7280;
  font-weight: 900;
`;

const Skeleton = styled.div`
  height: 280px;
  border-radius: 22px;
  border: 1px solid #e5e7eb;
  background: linear-gradient(90deg, #f3f4f6, #fafafa, #f3f4f6);
  background-size: 200% 100%;
  animation: shimmer 1.1s infinite linear;

  @keyframes shimmer {
    0% { background-position: 0% 0; }
    100% { background-position: 200% 0; }
  }
`;

/**
 * Förväntade fält i Firestore (golf_courses):
 * - name (string)
 * - holes (number)  <-- används för sortering 1..18
 * - imageUrl (string)
 * - type / category / courseType (string) ex "Bangolf" / "Äventyrsgolf"
 * - excerpt / shortDescription (string) (valfritt)
 */
export default function CoursesSection() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  // "ALL" | "BANGOLF" | "AVENTYR"
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const snap = await getDocs(collection(firestore, "golf_courses"));
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setCourses(data);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const normalizeType = (c) => {
    const raw = (c.type || c.category || c.courseType || "").toString().trim();
    const name = (c.name || "").toString().toLowerCase();

    const merged = `${raw} ${name}`.toLowerCase();

    if (merged.includes("äventyr") || merged.includes("aventyr")) return "Äventyrsgolf";
    if (merged.includes("bangolf")) return "Bangolf";

    // fallback om inget är angivet
    return "Bangolf";
  };

  const items = useMemo(() => {
    const normalized = (courses || []).map((c) => {
      const typeLabel = normalizeType(c);

      const subtitle =
        c.excerpt ||
        c.shortDescription ||
        (typeLabel === "Äventyrsgolf"
          ? "Lekfull och familjevänlig bana – perfekt för kalas och grupper."
          : "Klassisk bana för både nybörjare och tävlingsspel.");

      const holes = typeof c.holes === "number" ? c.holes : Number(c.holes);

      return {
        id: c.id,
        title: c.name || "Bana",
        holes: Number.isFinite(holes) ? holes : undefined,
        imageUrl: c.imageUrl || c.image || "",
        typeLabel,
        subtitle,
      };
    });

    // ✅ sortera 1..18 (holes stigande). Om holes saknas hamnar de sist.
    normalized.sort((a, b) => {
      const ah = Number.isFinite(a.holes) ? a.holes : 9999;
      const bh = Number.isFinite(b.holes) ? b.holes : 9999;
      return ah - bh;
    });

    // ✅ filter
    const filtered = normalized.filter((c) => {
      if (filter === "ALL") return true;
      if (filter === "BANGOLF") return c.typeLabel === "Bangolf";
      if (filter === "AVENTYR") return c.typeLabel === "Äventyrsgolf";
      return true;
    });

    return filtered;
  }, [courses, filter]);

  if (loading) {
    return (
      <Grid>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </Grid>
    );
  }

  return (
    <>
      <Toolbar>
        <Filters>
          <FilterBtn $active={filter === "ALL"} onClick={() => setFilter("ALL")}>
            Alla
          </FilterBtn>
          <FilterBtn $active={filter === "BANGOLF"} onClick={() => setFilter("BANGOLF")}>
            Bangolf
          </FilterBtn>
          <FilterBtn $active={filter === "AVENTYR"} onClick={() => setFilter("AVENTYR")}>
            Äventyrsgolf
          </FilterBtn>
        </Filters>

        <Count>Visar {items.length} banor</Count>
      </Toolbar>

      {!items.length ? (
        <Empty>Inga banor matchar filtret.</Empty>
      ) : (
        <Grid>
          {items.map((c) => (
            <CourseCard
              key={c.id}
              id={c.id}
              title={
                Number.isFinite(c.holes)
                  ? `${c.holes} – ${c.title}`
                  : c.title
              }
              subtitle={c.subtitle}
              imageUrl={c.imageUrl}
              holes={c.holes}
              typeLabel={c.typeLabel}
              to={`/banor?course=${c.id}`}
            />
          ))}
        </Grid>
      )}
    </>
  );
}
